/**
 * Form Data Persistence Utility
 * Saves form data to localStorage and restores it when needed
 */

const FORM_DATA_KEY_PREFIX = 'form_data_';
const RESTORE_SESSION_PREFIX = 'form_restore_done_';

/**
 * Whether we already restored this form type in the current browser session.
 */
export const hasConsumedFormRestore = (formType) => {
  try {
    return sessionStorage.getItem(`${RESTORE_SESSION_PREFIX}${formType}`) === '1';
  } catch {
    return false;
  }
};

/** Mark restore as done so remounts / strict mode do not re-apply drafts. */
export const markFormRestoreConsumed = (formType) => {
  try {
    sessionStorage.setItem(`${RESTORE_SESSION_PREFIX}${formType}`, '1');
  } catch {
    // ignore
  }
};

export const clearFormRestoreConsumed = (formType) => {
  try {
    sessionStorage.removeItem(`${RESTORE_SESSION_PREFIX}${formType}`);
  } catch {
    // ignore
  }
};

/**
 * Save form data to localStorage
 * @param {string} formType - Type of form (e.g., 'property', 'post', 'event', 'ad')
 * @param {Object} formData - Form data to save
 */
export const saveFormData = (formType, formData) => {
  try {
    const key = `${FORM_DATA_KEY_PREFIX}${formType}`;
    const dataToSave = {
      ...formData,
      timestamp: Date.now(),
      formType
    };
    localStorage.setItem(key, JSON.stringify(dataToSave));
    console.log(`Form data saved for ${formType}:`, dataToSave);
  } catch (error) {
    console.error('Error saving form data:', error);
  }
};

/**
 * Strip persistence metadata before merging into React form state.
 * @param {Object|null} parsedData - Result from loadFormData
 * @returns {Object|null} - Form fields only
 */
export const pickPersistedFormFields = (parsedData) => {
  if (!parsedData || typeof parsedData !== 'object') {
    return null;
  }
  const { timestamp, formType, ...fields } = parsedData;
  return fields;
};

/**
 * Load form data from localStorage
 * @param {string} formType - Type of form
 * @returns {Object|null} - Saved form data or null if not found
 */
export const loadFormData = (formType) => {
  try {
    const key = `${FORM_DATA_KEY_PREFIX}${formType}`;
    const savedData = localStorage.getItem(key);
    
    if (!savedData) {
      return null;
    }
    
    const parsedData = JSON.parse(savedData);
    
    // Check if data is not too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (Date.now() - parsedData.timestamp > maxAge) {
      clearFormData(formType);
      return null;
    }
    
    console.log(`Form data loaded for ${formType}:`, parsedData);
    return parsedData;
  } catch (error) {
    console.error('Error loading form data:', error);
    return null;
  }
};

/**
 * Clear form data from localStorage
 * @param {string} formType - Type of form
 */
export const clearFormData = (formType) => {
  try {
    const key = `${FORM_DATA_KEY_PREFIX}${formType}`;
    localStorage.removeItem(key);
    console.log(`Form data cleared for ${formType}`);
  } catch (error) {
    console.error('Error clearing form data:', error);
  }
};

/**
 * Check if there's saved form data
 * @param {string} formType - Type of form
 * @returns {boolean} - True if saved data exists
 */
export const hasSavedFormData = (formType) => {
  try {
    const key = `${FORM_DATA_KEY_PREFIX}${formType}`;
    const savedData = localStorage.getItem(key);
    
    if (!savedData) {
      return false;
    }
    
    const parsedData = JSON.parse(savedData);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    return Date.now() - parsedData.timestamp <= maxAge;
  } catch (error) {
    console.error('Error checking saved form data:', error);
    return false;
  }
};

/**
 * Get all saved form data (for debugging)
 * @returns {Object} - All saved form data
 */
export const getAllSavedFormData = () => {
  const savedData = {};
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(FORM_DATA_KEY_PREFIX)) {
        const formType = key.replace(FORM_DATA_KEY_PREFIX, '');
        savedData[formType] = loadFormData(formType);
      }
    }
  } catch (error) {
    console.error('Error getting all saved form data:', error);
  }
  
  return savedData;
};

/**
 * Clear all saved form data
 */
export const clearAllFormData = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(FORM_DATA_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('All form data cleared');
  } catch (error) {
    console.error('Error clearing all form data:', error);
  }
};
